import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { AuthFormType } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { Responsive } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules';
import { AuthModal } from 'app/shared/components/organisms';
import { APPLY_EVENT_ATTENDEE } from 'app/fan/graphql/eventAttendees/mutations';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { Drawer } from 'app/fan/components/molecules';
import { EventPageTemplate } from 'app/fan/components/templates';
import { ApplyFlowCheckoutWidget } from 'app/fan/components/pages/Event/components';
import { useApplyFlowPage } from 'app/fan/components/pages/Event/eventUtils';

interface ApplyFlowPreInviteProps {
  'data-qaid'?: string;
}

export const ApplyFlowPreInviteContainer: React.FC<
  React.PropsWithChildren<ApplyFlowPreInviteProps>
> = ({ 'data-qaid': qaId = 'event-page-apply-flow-pre-invite' }) => {
  const { dimensions } = useCurrentTheme();
  const { formatMessage } = useIntl();
  const { event, eventAttendee, refetchEventAttendee, isPlusOneFlow } =
    useEventPageContext();
  const {
    isCheckoutDrawerOpen,
    setIsCheckoutDrawerOpen,
    openCheckoutDrawer,
    onDrawerClose,
    numberOfTickets,
    setNumberOfTickets,
    onError,
  } = useApplyFlowPage();
  const { user, signIn } = useContext(AuthContext);

  const [isApplyForTicketsLoading, setIsApplyForTicketsLoading] =
    useState(false);
  const [applyForTickets] = useMutation(APPLY_EVENT_ATTENDEE);
  const [isAuthModalShowing, setIsAuthModalShowing] = useState(false);
  const [currentTabSelected, setCurrentTabSelected] =
    useState<AuthFormType>('signUp');
  const openAuthModal = () => {
    setIsAuthModalShowing(true);
  };

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect this to be present
  if (!event) {
    return null;
  }

  async function successfulSignIn() {
    signIn();
    setIsAuthModalShowing(false);
  }

  async function onClickApplyForTickets() {
    setIsApplyForTicketsLoading(true);

    try {
      await applyForTickets({
        variables: {
          eventId: Number(event?.id),
          numTickets: numberOfTickets,
        },
      });
      refetchEventAttendee && (await refetchEventAttendee());
      setIsCheckoutDrawerOpen(false);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      onError();
    }
    setIsApplyForTicketsLoading(false);
  }

  const hasApplied =
    !isPlusOneFlow && eventAttendee?.currentState === 'applied';
  const isPrimaryCtaDisabled = !event.isAppliable || hasApplied;

  const applyForTicketsText = formatMessage({
    id: 'event.applyFlow.applyForTickets',
  });
  const loginToApplyText = formatMessage({
    id: 'event.applyFlow.loginToApply',
  });
  const primaryCtaText = user ? applyForTicketsText : loginToApplyText;

  const checkoutWidgetProps = {
    event,
    numberOfTickets,
    setNumberOfTickets,
    primaryCtaText,
    onClickPrimaryCta: user ? onClickApplyForTickets : openAuthModal,
    isPrimaryCtaFullWidth: true,
    isPrimaryCtaDisabled,
    isPrimaryCtaLoading: isApplyForTicketsLoading,
    primaryCtaDataQaid: 'event-page-apply-flow-checkout-widget-apply-button',
  };

  return (
    <EventPageTemplate
      data-qaid={qaId}
      event={event}
      attendeeState={isPlusOneFlow ? undefined : eventAttendee?.currentState}
      isOutOfStock={false}
      ticketSaleStatus="available"
      secondColumn={<ApplyFlowCheckoutWidget {...checkoutWidgetProps} />}
    >
      <Box data-qaid="event-page-first-column-children">
        <Responsive hideUp="smallDesktop">
          <>
            <Box
              pt={8}
              pb={4}
              px={dimensions.horizontalPadding.pageGridItem}
              className="trigger"
            >
              <ActionButton
                fullWidth
                data-qaid="event-page-apply-flow-checkout-widget-trigger"
                onClick={user ? openCheckoutDrawer : openAuthModal}
                disabled={isPrimaryCtaDisabled}
                variant="primary"
              >
                {primaryCtaText}
              </ActionButton>
            </Box>
            <Drawer
              isOpen={isCheckoutDrawerOpen}
              setIsOpen={setIsCheckoutDrawerOpen}
              onClose={onDrawerClose}
            >
              <ApplyFlowCheckoutWidget {...checkoutWidgetProps} />
            </Drawer>
          </>
        </Responsive>
      </Box>
      <AuthModal
        data-qaid="apply-flow-pre-invite-auth-modal"
        isOpen={isAuthModalShowing}
        setIsOpen={setIsAuthModalShowing}
        onSuccess={successfulSignIn}
        eventId={event.id}
        city={event.city}
        parentContainer="EventV2 Page"
        currentTabSelected={currentTabSelected}
        setCurrentTabSelected={setCurrentTabSelected}
      />
    </EventPageTemplate>
  );
};

export const ApplyFlowPreInviteEventPage: React.FC<
  React.PropsWithChildren<ApplyFlowPreInviteProps>
> = ({ 'data-qaid': qaId }) => {
  const { event } = useEventPageContext();

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect this to be present
  if (!event) {
    return null;
  }

  return <ApplyFlowPreInviteContainer data-qaid={qaId} />;
};
